import { useEffect, useRef, useState } from 'react';
import * as api from '../../../../api/apiConnector.js';

/**
 * Custom hook for managing SSE-based lock on collaborative forms
 *
 * This hook establishes and maintains a Server-Sent Events (SSE) connection
 * to acquire and hold a lock on a collaborative form compilation.
 *
 * Key Features:
 * - Automatic lock acquisition via SSE connection
 * - Automatic lock release on component unmount or page close
 * - Handles lock conflicts (form already locked by another user)
 * - Receives server events (connected, ping, force_disconnect)
 *
 * @param {string} completionId - ID of the form completion to lock
 * @param {object} user - User object with authentication token
 * @param {boolean} isCollaborative - Whether the form is collaborative
 * @returns {object} Lock state object with following properties:
 *   - hasLock: Boolean indicating if current user has acquired the lock
 *   - isLocked: Boolean indicating if form is locked by another user
 *   - lockedBy: Object with info about the user who has the lock
 *   - connecting: Boolean indicating if connection is in progress
 *   - error: String with error message if lock acquisition failed
 */
const useFormLock = (completionId, user, isCollaborative) => {
  // State to track lock status
  const [state, setState] = useState({
    hasLock: false,        // True if current user has acquired the lock
    isLocked: false,       // True if form is locked by another user
    lockedBy: null,        // Info about the user who has the lock
    connecting: false,     // True while attempting to connect
    error: null           // Error message if lock failed
  });

  // Reference to AbortController for cleanup
  const abortControllerRef = useRef(null);
  // Track if connection has been attempted to prevent loops
  const connectionAttemptedRef = useRef(false);

  useEffect(() => {
    // Skip lock logic if form is not collaborative or no completion ID or no user
    if (!isCollaborative || !completionId || !user) {
      return;
    }

    let mounted = true;

    /**
     * Establishes SSE connection to acquire lock
     */
    const connect = async () => {
      // Prevent multiple connection attempts
      if (connectionAttemptedRef.current) {
        return;
      }
      connectionAttemptedRef.current = true;

      setState(prev => ({ ...prev, connecting: true, error: null }));

      try {
        // Create abort controller for cleanup
        abortControllerRef.current = new AbortController();

        // Initiate SSE connection to lock endpoint using API connector
        const response = await api.connectSSE('darm/formCompletion/lock/connect', {
          user: user,
          body: { completionId },
          signal: abortControllerRef.current.signal
        });

        // Handle 423 Locked response - form already locked by another user
        if (response.status === 423) {
          const data = await response.json();
          if (mounted) {
            setState({
              hasLock: false,
              isLocked: true,
              lockedBy: data.lockedBy,
              connecting: false,
              error: null
            });
          }
          return;
        }

        // Handle other error responses
        if (!response.ok) {
          throw new Error(`Lock failed: ${response.status}`);
        }

        // Lock acquired successfully
        if (mounted) {
          setState({
            hasLock: true,
            isLocked: false,
            lockedBy: null,
            connecting: false,
            error: null
          });
        }

        // Read SSE stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          // Decode and buffer incoming data
          buffer += decoder.decode(value, { stream: true });

          // Split by SSE message delimiter
          const messages = buffer.split('\n\n');
          buffer = messages.pop() || '';

          // Process each complete message
          for (const msg of messages) {
            if (msg.startsWith('data: ')) {
              try {
                const data = JSON.parse(msg.slice(6));

                // Handle force disconnect event from server
                if (data.type === 'force_disconnect') {
                  alert(data.message || 'Sei stato disconnesso');
                  // Redirect to forms list
                  window.location.href = '/';
                }

                // Handle actor_completed: server released the lock after actor submitted their part
                // The component will unmount on its own (navigate away), just close the reader
                if (data.type === 'actor_completed') {
                  reader.cancel();
                  return;
                }
              } catch (parseError) {
                console.error('Error parsing SSE message:', parseError);
              }
            }
          }
        }

      } catch (error) {
        // Ignore AbortError - this is expected during cleanup
        if (error.name === 'AbortError') return;

        console.error('Lock connection error:', error);

        if (mounted) {
          setState(prev => ({
            ...prev,
            connecting: false,
            error: error.message
          }));
        }
      }
    };

    // Start connection
    connect();

    // Cleanup function - automatically disconnects when component unmounts
    return () => {
      mounted = false;
      connectionAttemptedRef.current = false; // Reset on cleanup
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [completionId, user?.token, isCollaborative]); // Use user.token instead of user object to avoid unnecessary re-runs

  return state;
};

export default useFormLock;
